import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useHistory } from 'react-router';
import moment from 'moment';
import * as Yup from 'yup';
import { Form } from '@unform/web';
import { FormHandles, Scope } from '@unform/core';

import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  FormLabel,
  TablePagination,
  TextField,
  Switch,
  FormControlLabel,
} from '@mui/material';

import {
  Typography,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from '@material-ui/core';

import { DesktopDatePicker, LocalizationProvider } from '@mui/lab';

import AdapterDateFns from '@mui/lab/AdapterDateFns';

import { Visibility, AddCircle } from '@material-ui/icons';

import { useStyles, StyledTableCell } from './styles';

import Loader from '../../../components/Loader';
import Input from '../../../components/Input';
import Select from '../../../components/Select';
import FileUploader from '../../../components/FileUploader';
import Snackbar, { AlertComponentProps } from '../../../components/Snackbar';

import { Record } from '../../../models/record';
import { User } from '../../../models/user';
import { Patient } from '../../../models/patient';

import { useAxios } from '../../../utils/hooks';
import { getChip } from '../../../utils/getChip';
import { CreateRecordData, SelectProps } from './dto';
import getValidationErrors from '../../../utils/getValidationErrors';

function getSufix(label: string) {
  return label !== 'online' ? ' D' : '';
}

const Records: React.FC = () => {
  const api = useAxios();
  const classes = useStyles();
  const history = useHistory();

  const [loading, setLoading] = useState(true);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(4);

  const [alert, setAlert] = useState<
    AlertComponentProps & { isActive: boolean }
  >({
    isActive: false,
    type: 'info',
    message: 'none',
  });
  const formRef = useRef<FormHandles>(null);
  const [users, setUsers] = useState<SelectProps[]>([]);
  const [open, setOpen] = useState(false);

  const [patients, setPatients] = useState<SelectProps[]>([]);
  const [fileIds, setFileIds] = useState<string[]>([]);
  const [records, setRecords] = useState<Record[]>([]);
  const formFilterRef = useRef<FormHandles>(null);
  const [date, setDate] = useState<Date | null>(null);
  const [filterPatients, setFilterPatients] = useState<SelectProps[]>([]);
  const [numbers, setNumbers] = useState<SelectProps[]>([]);
  const [filters, setFilters] = useState('');

  const [filterTypes, setFilterTypes] = useState({
    type: '',
    status: '',
    patient: '',
    number: '',
    date: '',
    professional: '',
    outOfDate: '',
  });

  const resetAlert = () => {
    setAlert({
      isActive: false,
      type: 'info',
      message: 'none',
    });
  };

  const handleFile = useCallback(
    (fileId: string) => {
      setFileIds([...fileIds, fileId]);
    },
    [fileIds],
  );

  const handleClickOpen = useCallback(async () => {
    setLoading(true);

    try {
      const patientsResponse = await api.current?.get('/patients');

      if (patientsResponse?.data) {
        setPatients(
          patientsResponse.data.map((patient: Patient) => ({
            value: patient._id,
            label: patient.name,
          })),
        );
      }

      const usersResponse = await api.current?.get('/users');

      if (usersResponse?.data) {
        setUsers(
          usersResponse.data.map((user: User) => ({
            value: user.id,
            label: `${user.firstName} ${user.lastName}`,
          })),
        );
      }

      setAlert({
        isActive: true,
        type: 'success',
        message: 'Dados de formuário carregados com sucesso.',
      });
    } catch (error) {
      setAlert({
        isActive: true,
        type: 'error',
        message: 'Erro ao carregar dados.',
      });
    } finally {
      setOpen(true);
      setLoading(false);
    }
  }, [api]);

  const handleClose = () => {
    setOpen(false);
    setFileIds([]);
  };

  const handleChangePage = (event: any, newPage: any) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: any) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleSubmit = useCallback(
    async (data: CreateRecordData) => {
      try {
        resetAlert();
        formRef.current?.setErrors({});

        const schema = Yup.object().shape({
          number: Yup.number().required('Insira um número'),
          patient: Yup.string().required('Selecione um paciente'),
          professional_id: Yup.string().required(
            'Selecione um profissional responsável',
          ),
          supervisor_id: Yup.string().required('Selecione um supervisor'),
          type: Yup.string().required('Selecione o tipo de prontuário'),
          responsibles: Yup.array().of(
            Yup.object().shape({
              name: Yup.string().required('Digite o nome do responsável'),
              relation: Yup.string().required(
                'Digite a relação do responsável',
              ),
            }),
          ),
          teams: Yup.array().of(
            Yup.object().shape({
              action: Yup.string().required('Selecione a ação necessária'),
              professional: Yup.string().required('Selecione um profissional'),
            }),
          ),
        });

        await schema.validate(data, { abortEarly: false });

        data.number = Number(data.number);
        data.supervisor =
          users.find((u) => u.value === data.supervisor_id)?.label || '';
        data.professional =
          users.find((u) => u.value === data.professional_id)?.label || '';
        data.files = fileIds;

        const newRecord = await api.current?.post(`/records`, data);

        if (newRecord?.data) {
          delete newRecord.data.patient;
          newRecord.data.patient = {
            _id: data.patient,
            name: patients.find((p) => p.value === data.patient)?.label,
          };
        }

        setRecords([...records, newRecord?.data]);
        setAlert({
          isActive: true,
          type: 'success',
          message: 'Prontuário criado com sucesso.',
        });
        handleClose();
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);

          formRef.current?.setErrors(errors);
        } else {
          setAlert({
            isActive: true,
            type: 'error',
            message: 'Erro ao criar prontuário.',
          });
        }
      }
    },
    [users, records, patients, fileIds, api],
  );

  useEffect(() => {
    async function getRecords() {
      try {
        const recordsResponse = await api.current?.get(`/records?${filters}`);

        setRecords(recordsResponse?.data);

        setFilterPatients(
          recordsResponse?.data.map((record: Record) => {
            if (!record.patient) {
              return {
                value: null,
                label: 'Paciente não atribuido.',
              };
            }

            return {
              value: record.patient._id,
              label: record.patient.name,
            };
          }),
        );

        setNumbers(
          recordsResponse?.data.map((record: Record) => {
            return {
              value: String(record.number),
              label: record.number,
            };
          }),
        );
      } catch (error) {
        setAlert({
          isActive: true,
          type: 'error',
          message: 'Erro ao carregar Prontuários Ceapia.',
        });
      } finally {
        setLoading(false);
      }
    }

    getRecords();
  }, [api, filters]);

  useEffect(() => {
    async function getUsers() {
      try {
        const usersResponse = await api.current?.get('/users');

        if (usersResponse?.data) {
          setUsers(
            usersResponse.data.map((user: User) => ({
              value: user.id,
              label: `${user.firstName} ${user.lastName}`,
            })),
          );
        }
      } catch (error) {
        setAlert({
          isActive: true,
          type: 'error',
          message: 'Erro ao carregar terapeutas.',
        });
      }
    }

    getUsers();
  }, [api]);

  const handleFilters = useCallback(
    (type: string, data: any) => {
      setLoading(true);

      switch (type) {
        case 'type':
          setFilterTypes({
            ...filterTypes,
            type: data && data.value ? `&type=${data.value}` : '',
          });
          break;
        case 'status':
          setFilterTypes({
            ...filterTypes,
            status: data && data.value ? `&status=${data.value}` : '',
          });
          break;
        case 'patient':
          setFilterTypes({
            ...filterTypes,
            patient: data && data.value ? `&patient=${data.value}` : '',
          });
          break;
        case 'number':
          setFilterTypes({
            ...filterTypes,
            number: data && data.value ? `&number=${data.value}` : '',
          });
          break;
        case 'date':
          setFilterTypes({
            ...filterTypes,
            date: data ? `&date=${moment(data).format('YYYY-MM-DD')}` : '',
          });
          break;
        case 'professional':
          setFilterTypes({
            ...filterTypes,
            professional:
              data && data.value ? `&professional_id=${data.value}` : '',
          });
          break;
        case 'out_of_date':
          setFilterTypes({
            ...filterTypes,
            outOfDate: data ? `&is_out_of_date=true` : '',
          });
          break;
        default:
          setLoading(false);
          break;
      }
    },
    [filterTypes],
  );

  const handleOutOfDate = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleFilters('out_of_date', event.target.checked);
  };

  useEffect(() => {
    setFilters(
      filterTypes.type +
        filterTypes.status +
        filterTypes.patient +
        filterTypes.number +
        filterTypes.date +
        filterTypes.professional +
        filterTypes.outOfDate,
    );

    setPage(0);
  }, [filterTypes]);

  return (
    <>
      <Loader loading={loading} />

      <Form ref={formFilterRef} initialData={{}} onSubmit={handleFilters}>
        <div className={classes.header}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddCircle />}
            className={classes.addButton}
            onClick={handleClickOpen}
          >
            Adicionar Prontuário
          </Button>
        </div>

        <Paper elevation={0} className={classes.filterCard}>
          <Grid container spacing={2} alignItems="flex-end">
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <span className={classes.filterLabel}>Tipo</span>
              <Select
                name="type"
                isMulti={false}
                placeholder="Tipo"
                isClearable
                className={classes.filterField}
                onChange={(e) => handleFilters('type', e)}
                options={[
                  { value: 'online', label: 'Online' },
                  { value: 'scanned', label: 'Digitalizado' },
                ]}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4} lg={2}>
              <span className={classes.filterLabel}>Status</span>
              <Select
                name="status"
                isMulti={false}
                placeholder="Status"
                isClearable
                className={classes.filterField}
                onChange={(e) => handleFilters('status', e)}
                options={[
                  { value: 'active', label: 'Ativo' },
                  { value: 'closed', label: 'Encerrado' },
                ]}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4} lg={2}>
              <span className={classes.filterLabel}>Paciente</span>
              <Select
                name="patient"
                isMulti={false}
                placeholder="Paciente"
                isClearable
                className={classes.filterField}
                onChange={(e) => handleFilters('patient', e)}
                options={filterPatients.filter((patient) => patient.value)}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4} lg={2}>
              <span className={classes.filterLabel}>Número</span>
              <Select
                name="number"
                isMulti={false}
                placeholder="Número"
                isClearable
                isSearchable
                className={classes.filterField}
                onChange={(e) => handleFilters('number', e)}
                options={numbers}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4} lg={2}>
              <span className={classes.filterLabel}>Terapeutas</span>
              <Select
                name="professional"
                isMulti={false}
                placeholder="Terapeutas"
                isClearable
                isSearchable
                className={classes.filterField}
                onChange={(e) => handleFilters('professional', e)}
                options={users}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4} lg={2}>
              <span className={classes.filterLabel}>Data</span>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DesktopDatePicker
                  inputFormat="dd/MM/yyyy"
                  value={date}
                  onChange={(d) => {
                    setDate(d);
                    handleFilters('date', d);
                  }}
                  renderInput={(params) => (
                    <TextField {...params} size="small" fullWidth />
                  )}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12} sm={6} md={4} lg={2}>
              <FormControlLabel
                className={classes.toggleField}
                control={
                  // eslint-disable-next-line react/jsx-wrap-multilines
                  <Switch color="primary" onChange={handleOutOfDate} />
                }
                label="Desatualizados"
              />
            </Grid>
          </Grid>
        </Paper>
      </Form>

      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="form-dialog-title"
      >
        <Form
          ref={formRef}
          initialData={{
            number: Math.floor(Math.random() * 100000),
          }}
          onSubmit={handleSubmit}
        >
          <DialogTitle id="form-dialog-title">Adicionar Prontuário</DialogTitle>
          <DialogContent style={{ minHeight: '70vh' }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={4}>
                <Input
                  autoFocus
                  id="number"
                  type="number"
                  label="Número"
                  name="number"
                />
              </Grid>

              <Grid item xs={8} />

              <Grid item xs={4}>
                <Select
                  name="type"
                  isMulti={false}
                  placeholder="Tipo"
                  options={[
                    { value: 'online', label: 'Online' },
                    { value: 'scanned', label: 'Digitalizado' },
                  ]}
                />
              </Grid>

              <Grid item xs={8}>
                <Select
                  name="patient"
                  label="Paciente"
                  isMulti={false}
                  placeholder="Nome do Paciente"
                  options={patients}
                />
              </Grid>

              <Grid item xs={6}>
                <Select
                  name="professional_id"
                  isMulti={false}
                  placeholder="Profissional"
                  options={users}
                />
              </Grid>

              <Grid item xs={6}>
                <Select
                  name="supervisor_id"
                  isMulti={false}
                  placeholder="Supervisor"
                  options={users}
                />
              </Grid>

              <Grid item xs={12}>
                <FormLabel className={classes.formTitles}>
                  Responsáveis e Familiares
                </FormLabel>
              </Grid>

              <Scope path="responsibles[0]">
                <Grid item xs={4}>
                  <Input id="relation" label="Relação" name="relation" />
                </Grid>

                <Grid item xs={6}>
                  <Input id="name" label="Nome" name="name" />
                </Grid>
              </Scope>

              <Grid item xs={2}>
                <Button variant="text">+</Button>
              </Grid>

              <Grid item xs={12}>
                <FormLabel className={classes.formTitles}>
                  Equipe de Atendimento
                </FormLabel>
              </Grid>

              <Scope path="teams[0]">
                <Grid item xs={4}>
                  <Select
                    name="action"
                    isMulti={false}
                    placeholder="Ação"
                    options={users}
                  />
                </Grid>

                <Grid item xs={6}>
                  <Select
                    name="professional"
                    isMulti={false}
                    placeholder="Professional"
                    options={users}
                  />
                </Grid>
              </Scope>

              <Grid item xs={2}>
                <Button variant="text">+</Button>
              </Grid>

              <Grid item xs={12}>
                <FileUploader handleFileId={handleFile} />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancelar</Button>
            <Button type="submit" color="primary" variant="contained">
              Salvar
            </Button>
          </DialogActions>
        </Form>
      </Dialog>

      <TableContainer
        component={Paper}
        style={{ display: !loading ? '' : 'none', marginBottom: '2%' }}
      >
        <Table className={classes.table}>
          <TableHead>
            <TableRow>
              <StyledTableCell>Terapeuta</StyledTableCell>
              {/* <StyledTableCell>Supervisor</StyledTableCell> */}
              <StyledTableCell>Paciente</StyledTableCell>
              <StyledTableCell>Número</StyledTableCell>
              <StyledTableCell>Última Atualização</StyledTableCell>
              <StyledTableCell>Status</StyledTableCell>
              <StyledTableCell>Tipo</StyledTableCell>
              <StyledTableCell align="center">Visualizar</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!records.length && (
              <TableRow>
                <TableCell component="th" scope="row" colSpan={7}>
                  <Typography>Nenhum prontuário encontrado.</Typography>
                </TableCell>
              </TableRow>
            )}

            {(rowsPerPage > 0
              ? records.slice(
                  page * rowsPerPage,
                  page * rowsPerPage + rowsPerPage,
                )
              : records
            ).map((record) => (
              <TableRow key={record._id}>
                <TableCell component="th" scope="row">
                  <Tooltip title={record?.professional.join(', ')}>
                    <span>
                      {`${record?.professional[0] || '--'}${
                        record?.professional.length > 1 ? ', ...' : ''
                      }`}
                    </span>
                  </Tooltip>
                </TableCell>
                {/* <TableCell component="th" scope="row">
                  <Tooltip title={record?.supervisor?.join(', ') || ''}>
                    <span>
                      {`${record?.supervisor[0] || '--'}${
                        record?.supervisor.length > 1 ? ', ...' : ''
                      }`}
                    </span>
                  </Tooltip>
                </TableCell> */}

                <TableCell>
                  {record?.patient?.name || '--'}

                  {record?.patient2 && ` / ${record?.patient2?.name}`}
                </TableCell>

                <TableCell>
                  {`${record.number}${getSufix(record.type)}`}
                </TableCell>
                <TableCell>
                  {moment(record.updatedAt).format('DD/MM/YYYY HH:mm')}
                </TableCell>
                <TableCell>{getChip(record.status)}</TableCell>
                <TableCell>{getChip(record.type)}</TableCell>
                <TableCell align="center">
                  <IconButton
                    onClick={() => {
                      history.push(`/prontuarios/${record._id}`);
                    }}
                    color="primary"
                  >
                    <Visibility />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 100]}
          component="div"
          count={records.length}
          rowsPerPage={rowsPerPage}
          labelRowsPerPage={<span>Prontuários por página:</span>}
          labelDisplayedRows={({ count, from, to }) => {
            return `${from} - ${to} de ${count}`;
          }}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      {alert.isActive && <Snackbar {...alert} />}
    </>
  );
};

export default Records;
